import { cn } from "@/lib/utils";
import { Id } from "@hello-pangea/dnd";
import { GlowEffect } from "@/components/global-ui/glow-effect";

// Dummy giveaway data
type DummyGiveawayType = {
  _id: Id<"giveaways">;
  _creationTime: number;
  termsAndConditions?: string | undefined;
  bannerImageId?: Id<"_storage"> | undefined;
  bannerImageUrl?: string | undefined;
  useShortUrl?: boolean | undefined;
  shortUrlId?: Id<"shortUrls"> | undefined;
  orgId: string;
  userId: string;
  title: string;
  description: string;
  siteId: string;
  createdAt: string;
  updatedAt: string;
  status: "draft" | "published" | "closed";
  startDate: string;
  endDate: string;
  rewardDescription: string;
  entryActions: {
    actionId: Id<"giveawayActions">;
    title: string;
    actionUrl: string;
    points: number;
    position: number;
    type: "universal" | "custom" | "viral_share";
    isExpandable: boolean;
    inputConfig?: {
      type: "username" | "code" | "none";
      label: string;
      platform?: string;
      verificationData?: {
        code?: string;
        validUsernames?: string[];
        customValidator?: string;
      };
    };
    iconPath?: string;
  }[];
  totalParticipants: number;
  totalEntries: number;
  daysLeft: number;
  yourEntries: number;
};
const dummyGiveaways: DummyGiveawayType[] = [{
  _id: "giveaways/1" as Id<"giveaways">,
  _creationTime: 1717000000,
  orgId: "1",
  userId: "1",
  title: "Get a $500 Gift Card!",
  siteId: "1",
  createdAt: "2021-01-01",
  updatedAt: "2021-01-01",
  status: "published",
  startDate: "2021-01-01",
  endDate: "2021-01-01",
  rewardDescription: "Win a $500 Gift Card!",
  entryActions: [],
  totalParticipants: 850,
  totalEntries: 2589,
  daysLeft: 19,
  yourEntries: 3,
  description: "Enter to win a gift card to your favorite store!",
  bannerImageUrl: "/images/gift-card-image.jpg"
}, {
  _id: "giveaways/2" as Id<"giveaways">,
  _creationTime: 1717000000,
  orgId: "1",
  userId: "1",
  title: "Win a MacBook Pro M3!",
  siteId: "1",
  createdAt: "2021-01-01",
  updatedAt: "2021-01-01",
  status: "published",
  startDate: "2025-07-01",
  endDate: "2025-07-31",
  rewardDescription: "Win a MacBook Pro M3!",
  entryActions: [],
  totalParticipants: 1247,
  totalEntries: 3891,
  daysLeft: 27,
  yourEntries: 329,
  description: "Join our launch celebration and win amazing prizes!",
  bannerImageUrl: "/images/laptop-image.jpg"
}, {
  _id: "giveaways/3" as Id<"giveaways">,
  _creationTime: 1717000000,
  orgId: "1",
  userId: "1",
  title: "Win a Trip to Bali!",
  siteId: "1",
  createdAt: "2021-01-01",
  updatedAt: "2021-01-01",
  status: "published",
  startDate: "2021-01-01",
  endDate: "2021-01-01",
  rewardDescription: "Win a Trip to Bali!",
  entryActions: [],
  totalParticipants: 2100,
  totalEntries: 5720,
  daysLeft: 21,
  yourEntries: 14,
  description: "Dream vacation awaits! Enter now!",
  bannerImageUrl: "/images/bali-night-image.jpg"
}];

// Giveaway Preview Component
export default function GiveawayPreviewDummy() {
  return <div className="group relative z-[20] h-full w-full" data-sentry-component="GiveawayPreviewDummy" data-sentry-source-file="giveaway-preview-dummy.tsx">
      {/* Debug: Simple visible content first */}
      <div className="-ml-14 flex h-full min-h-[418px] w-full max-w-[756px] pt-10">
        {" "}
        {/* Left Fade Overlay */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 left-0 top-0 z-10 h-full w-20 bg-gradient-to-r from-black via-transparent to-transparent" />
        {/* Right Fade Overlay */}
        <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 h-full w-20 bg-gradient-to-l from-black via-transparent to-transparent" />
        <div className="flex gap-4">
          {dummyGiveaways.map((giveaway, index) => <div key={index} className="relative">
              {index === 1 && <>
                  {/* Top side glow */}
                  <GlowEffect colors={["#f97316", "#c2410c", "#fb923c", "#fca5a5", "#ef4444"]} sides={["top"]} mode="colorShift" blur="soft" duration={4} className="absolute left-[6%] right-[2%] top-0 mt-10 h-0.5 w-[93%] rounded-full opacity-50 transition-all duration-300 group-hover:mt-[15px] group-hover:h-2.5 group-hover:scale-105 group-hover:opacity-100 group-hover:duration-700" />
                  {/* Right side glow */}
                  <GlowEffect colors={["#f97316", "#c2410c", "#fb923c", "#fca5a5", "#ef4444"]} sides={["right"]} mode="colorShift" blur="soft" duration={4} className="absolute right-0 top-[5%] mt-10 h-[95%] w-0.5 rounded-full opacity-50 transition-all duration-300 group-hover:-mr-1.5 group-hover:mt-[13px] group-hover:w-2.5 group-hover:scale-105 group-hover:opacity-100 group-hover:duration-700" />
                </>}
              <div key={index} className={cn("z-10 mt-10 flex-shrink-0 rounded-3xl border-r-1 border-t-1 border-zinc-700 transition-all duration-500 group-hover:mt-5", index === 0 && "scale-75 opacity-70 group-hover:border-zinc-500", index === 1 && "scale-100 opacity-70 group-hover:scale-105 group-hover:border-zinc-400 group-hover:opacity-100", index === 2 && "scale-75 opacity-70 group-hover:border-zinc-500")} style={{
            width: "280px"
          }}>
                {/* <BackgroundGradient
                 containerClassName={cn(
                  "max-w-xl overflow-hidden rounded-3xl transition-all duration-500",
                  index === 1 &&
                    "group-hover:scale-105 transition-all duration-500 group-hover:overflow-visible",
                 )}
                 animate={true}
                 > */}

                <div className="rounded-3xl border border-gray-700 bg-gray-800 p-4 shadow-lg">
                  {/* Stats */}
                  <div className="mb-3 grid grid-cols-3 gap-2 text-center text-xs">
                    <div>
                      <div className="font-bold text-white">
                        {giveaway.yourEntries}
                      </div>
                      <div className="text-gray-400">Your Entries</div>
                    </div>
                    <div>
                      <div className="font-bold text-white">
                        {giveaway.totalEntries}
                      </div>
                      <div className="text-gray-400">Total Entries</div>
                    </div>
                    <div>
                      <div className="font-bold text-white">
                        {giveaway.daysLeft}
                      </div>
                      <div className="text-gray-400">Days Left</div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="mb-3 text-center text-lg font-bold text-white">
                    {giveaway.title}
                  </h3>

                  {/* Banner Image */}
                  {giveaway.bannerImageUrl && <div className="mb-3 h-24 w-full overflow-hidden rounded-lg">
                      <img src={giveaway.bannerImageUrl} alt={giveaway.title} className="h-full w-full object-cover" />
                    </div>}

                  {/* Description */}
                  <p className="mb-3 text-xs text-gray-300">
                    {giveaway.description}
                  </p>

                  {/* Reward */}
                  <div className="rounded-lg bg-blue-900/20 p-3">
                    <h4 className="text-sm font-semibold text-white">Reward</h4>
                    <p className="text-xs text-gray-300">
                      {giveaway.rewardDescription}
                    </p>
                  </div>
                </div>
                {/* </BackgroundGradient> */}
              </div>
            </div>)}
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 z-20 min-h-16 w-full rounded-b-3xl bg-gradient-to-t from-black via-black/95 to-transparent px-6 pb-6 pt-16">
        <div>
          <h3 className="text-xl font-bold text-white">Viral Giveaways</h3>
          <p className="text-gray-400">
            Create viral giveaways that turn your customers into your marketing
            team and grow 10x faster.
          </p>
        </div>
      </div>
    </div>;
}